import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { TICKET_STATUSES } from "utils/helpers/enums/shared.enum";
import useHost from "../useHooks";
import Pagination from "components/pagination";
import { EyeIconTable } from "assets/images/svgs";
import Breadcrumb, { BreadcrumbLink } from "components/breadcrumbs";
import { siteRoutes } from "utils/helpers/enums/routes";
import ChatModal from "./component/ChatModal";
import CancelEventNotFoundGif from "assets/images/gifs/cancel-event-not-found.gif";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import useStore from "hooks/useStore";

// import ConfirmationModal from "../component/ConfirmationModal";
class UserFiltersDTO {
  page: number = 0;
  size: number = 10;
  status?: string = undefined;
}

const MyCancelledTickets: React.FC = () => {
  const breadcrumbLinks: BreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.home },
    { title: "My Cancelled Tickets", path: siteRoutes.myCancelledTickets },
  ];
  const navigate = useNavigate();
  const { isProfileCompleted } = useStore();
  const [data, setData] = useState<any[]>([]);
  const { getCancelEventsTicketsListing } = useHost();

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (!profileCompleted) {
      navigate(siteRoutes.home, { replace: true });
    }
  }, [isProfileCompleted, navigate]);

  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const [filters, setFilters] = useState<UserFiltersDTO>(new UserFiltersDTO());
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);

  const [totalElements, setTotalElements] = useState<number>(0);
  const [selectedTicket, setSelectedTicket] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [showApproveConfirmation, setShowApproveConfirmation] =
    useState<boolean>(false);

  const columns: string[] = [
    "Event Title",
    "Reason",
    "Penalty Amount",
    "Status",
    "Created Date",
    "Action",
  ];

  const handlePageChange = (pageInfo: { selected: number; limit: number }) => {
    const updatedFilters = {
      ...filters,
      page: pageInfo.selected,
      size: pageInfo.limit,
    };
    setFilters(updatedFilters);
    getTicketListing(updatedFilters);
  };

  const getTicketListing = (filters: UserFiltersDTO) => {
    const queryParams: any = {};
    for (let key in filters) {
      const value = filters[key];
      // Include page and size always, include status only if it has a value
      if (key === "page" || key === "size" || (value !== undefined && value !== "" && value !== null)) {
        queryParams[key] = value;
      }
    }
    getCancelEventsTicketsListing(
      setData,
      queryParams,
      setTotalElements
    );
    
    // Mark initial load as complete after first API call
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  };

  useEffect(() => {
    getTicketListing(filters);
  }, []);

  const handleChangeFilter = (event: any) => {
    const {
      target: { name, value },
    } = event;
    setFilters({ ...filters, [name]: value });
  };

  const resetFilters = () => {
    const resetFiltersModel = new UserFiltersDTO();
    setFilters(resetFiltersModel);
    getTicketListing(resetFiltersModel);
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return "0";
    return `$${amount.toFixed(2)}`;
  };

  const truncateText = (text: string, maxLength: number = 50): string => {
    if (!text) return "-";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const handleOpenModal = (ticket: any) => {
    console.log("handleOpenModal called with ticket:", ticket);
    console.log("Current modal state - isModalOpen:", isModalOpen);
    if (ticket) {
      setSelectedTicket(ticket);
      setIsModalOpen(true);
      setShowApproveConfirmation(false);
      console.log("Modal should be open now");
    } else {
      console.error("Ticket is null or undefined");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setShowApproveConfirmation(false);
    setSelectedTicket(null);
  };

  return (
    <div className="container min-h-screen">
      <Breadcrumb links={breadcrumbLinks} />
      <h1 className="tex-2xl sm:text-[32px] text-primaryText font-bold mb-6">
        Cancel Events
      </h1>

      <div className="bg-coolLightGrayBg border  border-borderLightGray p-4 rounded-lg w-full my-6">
        <div className="w-full overflow-x-auto scrollbar-hidden">
          <div className="flex flex-wrap sm:flex-nowrap lg:justify-end items-center gap-3">
            {/* Status  */}
            <select
              value={filters.status || ""}
              onChange={handleChangeFilter}
              name="status"
              className="px-3 border border-lightGrayBorder text-primaryText outline-none rounded-lg text-sm bg-white h-[46px] w-full sm:w-auto min-w-[140px]"
            >
              <option value="">All Status</option>
              {Object.values(TICKET_STATUSES).map((status, index) => {
                return (
                  <option value={status} key={index}>
                    {status
                      .split("_")
                      .map(
                        (word: string) =>
                          word.charAt(0).toUpperCase() +
                          word.slice(1).toLowerCase()
                      )
                      .join(" ")}
                  </option>
                );
              })}
            </select>

            {/* Apply Filters Button */}
            <button
              onClick={() => getTicketListing(filters)}
              className="px-4 py-2 bg-darkTealBlue text-white rounded-lg text-sm h-[46px] w-full md:w-auto min-w-[140px]"
            >
              Apply Filters
            </button>

            {/* Clear Filters Button */}
            <button
              onClick={resetFilters}
              className="px-4 py-2 bg-techGlowBg text-primaryText rounded-lg text-sm h-[46px] w-full md:w-auto min-w-[140px]"
            >
              Clear Filter
            </button>
          </div>
        </div>
      </div>

      {data.length > 0 && (
        <div className="w-full flex flex-col gap-4">
          <div className="bg-white rounded-t-lg  border border-mediumGrayBorder overflow-x-auto">
            <table className="min-w-full divide-y divide-mediumGrayBorder">
              {/* Table Header */}
              <thead className="bg-white">
                <tr className="text-left">
                  {columns.map((column, index) => {
                    return (
                      <th
                        className="px-4 py-4 text-sm font-bold text-left text-primaryText whitespace-nowrap"
                        key={index}
                      >
                        {column}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              {/* Table Body */}
              <tbody className="bg-white divide-y divide-mediumGrayBorder">
                {data.map((ticket: any, index) => {
                  return (
                    <tr key={ticket.id || index} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm text-primaryText font-semibold whitespace-nowrap">
                        {ticket?.event?.title || "-"}
                      </td>

                      <td className="px-4 py-4 text-sm text-primaryText whitespace-nowrap">
                        <div className="max-w-xs" title={ticket?.reason || ""}>
                          {truncateText(ticket?.reason, 50)}
                        </div>
                      </td>

                      <td className="px-4 py-4 text-sm text-primaryText font-semibold whitespace-nowrap">
                        {ticket?.status === "pending"
                          ? "-"
                          : formatCurrency(ticket?.penaltyAmount)}
                      </td>
                      <td className="px-4 py-4 text-left whitespace-nowrap">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            ticket?.status === "pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : ticket?.status === "approved"
                              ? "bg-green-100 text-green-800"
                              : ticket?.status === "rejected"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {ticket?.status
                            ? ticket.status
                                .split("_")
                                .map(
                                  (word: string) =>
                                    word.charAt(0).toUpperCase() +
                                    word.slice(1).toLowerCase()
                                )
                                .join(" ")
                            : ""}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-primaryText whitespace-nowrap">
                        {ticket.createdAt
                          ? new Date(ticket.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )
                          : "N/A"}
                      </td>
                      <td className="px-4 py-4 text-left whitespace-nowrap w-[124px]">
                        <div className="flex justify-start gap-2">
                          <button
                            onClick={() => {
                              console.log("Eye icon clicked, ticket:", ticket);
                              handleOpenModal(ticket);
                            }}
                            className="cursor-pointer"
                            type="button"
                            aria-label="View details"
                          >
                            <EyeIconTable />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CustomMessageDisplay
        show={!isLoading && !data.length && !isInitialLoad}
        title="Great News!"
        slogan="Great news! You haven’t cancelled any event. All your events are on track."
        image={CancelEventNotFoundGif}
        imageAlt="No cancelled tickets found"
        className="py-12"
        imageClassName="w-[150px] h-[120px]"
        titleClassName="text-2xl font-bold text-primaryText"
        sloganClassName="text-gray-500 text-base"
      />

      {data?.length > 0 && (
        <Pagination
          key={`pagination-${filters.page}-${filters.size}`}
          count={totalElements}
          page={filters.page}
          limit={filters.size}
          onPageChange={handlePageChange}
          activeBgColor={"#DE9C27"}
          activeTextColor={"#ffffff"}
        />
      )}
      {isModalOpen && (
        <ChatModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          ticket={selectedTicket}
        />
      )}
    </div>
  );
};

export default MyCancelledTickets;
